// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { InfoOutlined } from '@/constants/hardfin-standard-icons';
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import { PropsWithChildren } from 'react';

const FilterCheckbox = styled(Checkbox)(({ theme }) => ({
  paddingTop: 0,
  paddingBottom: 0,
  '&.MuiCheckbox-indeterminate': {
    color: theme.palette.primary.light,
  },
}));

interface Props {
  /**
   * If provided, the menu item will have additional padding based on the level.
   */
  level?: number;

  /**
   * If true, the checkbox is checked.
   */
  isActive: boolean;

  /**
   * If true, the checkbox is in an indeterminate state.
   */
  isIndeterminate?: boolean;

  /**
   * Optional color for the text, using MUI's color system.
   */
  color?: string;

  /**
   * Optional example text to show in a tooltip when hovering over the info icon.
   */
  example?: string;

  /**
   * A callback fired when the checkbox is toggled.
   */
  onChange: (isActive: boolean) => void;
}

/**
 * A menu item with a checkbox.
 */
export function FilterMenuItem(props: PropsWithChildren<Props>) {
  const level = props.level ?? 0;
  const marginLeft = 1 + level * 2;

  const handleChange = (_: unknown, isChecked: boolean) => {
    props.onChange(isChecked);
  };

  return (
    <MenuItem
      dense
      disableGutters
      sx={{ marginLeft, marginRight: 1, borderRadius: 1 }}
    >
      <FormControlLabel
        checked={props.isActive}
        onChange={handleChange}
        sx={{
          paddingX: 1,
          flexGrow: 1,
          minWidth: 0,
          // Lets a long label shrink and ellipsize instead of widening the menu
          '& .MuiFormControlLabel-label': { minWidth: 0 },
        }}
        label={
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Typography
              component="div"
              variant="body2"
              color={props.color}
              minWidth={0}
            >
              {props.children}
            </Typography>
            {props.example && (
              <Tooltip
                title={props.example}
                placement="right"
                arrow
                enterDelay={200}
              >
                <IconButton
                  size="small"
                  sx={{
                    padding: 0,
                    minWidth: 16,
                    width: 16,
                    height: 16,
                    ml: 1,
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'text.primary',
                    },
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <InfoOutlined sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        }
        control={
          <FilterCheckbox
            disableRipple
            size="small"
            indeterminate={props.isIndeterminate}
          />
        }
      />
    </MenuItem>
  );
}
